import React from "react";
import {
  Check,
  BadgeCheck,
  Instagram,
  Heart,
  Eye,
  Users,
} from "lucide-react";
import { formatMetric, formatEngagement } from "../lib/formatters";
import type { InfluencerPreview } from "../types";

interface InfluencerSelectionListProps {
  influencers: InfluencerPreview[];
  selectedInfluencerIds: string[];
  alreadyAssignedIds: string[];
  onToggleInfluencer: (id: string) => void;
  onClearAll: () => void;
  onSelectAll: () => void;
}

export const InfluencerSelectionList: React.FC<InfluencerSelectionListProps> = ({
  influencers,
  selectedInfluencerIds,
  alreadyAssignedIds,
  onToggleInfluencer,
  onClearAll,
  onSelectAll,
}) => {
  const activeSelectedCount = selectedInfluencerIds.length;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="font-bold text-slate-900 text-sm">
          Selected Influencers ({activeSelectedCount})
        </h3>
        {activeSelectedCount > 0 ? (
          <button
            type="button"
            onClick={onClearAll}
            className="text-xs font-semibold text-[#A655F8] hover:text-[#9333EA] hover:underline cursor-pointer transition-colors"
          >
            Clear All
          </button>
        ) : (
          <button
            type="button"
            onClick={onSelectAll}
            className="text-xs font-semibold text-[#A655F8] hover:text-[#9333EA] hover:underline cursor-pointer transition-colors"
          >
            Select All
          </button>
        )}
      </div>

      {/* Scrollable Influencers List without visible scrollbar */}
      <div className="space-y-2.5 max-h-[310px] overflow-y-auto no-scrollbar">
        {influencers.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-slate-200 rounded-2xl text-slate-400 text-sm">
            No influencers selected.
          </div>
        ) : (
          influencers.map((inf) => {
            const isSelected = selectedInfluencerIds.includes(inf.id);
            const isAlreadyAssigned = alreadyAssignedIds.includes(inf.id);
            const categoryName = inf.category || "Influencer";

            return (
              <div
                key={inf.id}
                onClick={() => onToggleInfluencer(inf.id)}
                className={`group relative rounded-2xl border p-3 flex items-center gap-3 transition-all cursor-pointer ${
                  isSelected
                    ? "border-slate-200 bg-white hover:border-[#A655F8]/50 shadow-xs"
                    : "border-slate-100 bg-slate-50/50 opacity-60 hover:opacity-100"
                }`}
              >
                {/* Checkbox */}
                <div
                  className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-colors ${
                    isSelected
                      ? "bg-[#A655F8] text-white"
                      : "border-2 border-slate-300 bg-white"
                  }`}
                >
                  {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                </div>

                {/* Avatar */}
                <div className="w-11 h-11 rounded-full overflow-hidden border border-slate-100 bg-slate-100 shrink-0 flex items-center justify-center">
                  {inf.profilePhoto?.startsWith("http") ? (
                    <img
                      src={inf.profilePhoto}
                      alt={inf.realName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-tr from-[#FAF5FF] to-[#F3E8FF] text-[#A655F8] font-bold text-sm flex items-center justify-center">
                      {inf.realName
                        ? inf.realName.charAt(0).toUpperCase()
                        : "I"}
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-bold text-slate-900 text-sm truncate max-w-[140px]">
                      {inf.realName}
                    </span>
                    {inf.verified !== false && (
                      <BadgeCheck className="w-4 h-4 text-blue-500 fill-blue-500 text-white shrink-0" />
                    )}
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#FAF5FF] text-[#A655F8] border border-[#F3E8FF] truncate">
                      {categoryName}
                    </span>
                  </div>

                  <div className="text-xs text-slate-400 mt-0.5 truncate">
                    {inf.handle.startsWith("@")
                      ? inf.handle
                      : `@${inf.handle}`}
                  </div>

                  {/* Metrics Row */}
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                    <div className="flex items-center gap-1">
                      <Instagram className="w-3.5 h-3.5 text-pink-500" />
                      <span className="font-semibold text-slate-700">
                        {formatMetric(inf.followers)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Heart className="w-3.5 h-3.5 text-rose-400 fill-rose-50" />
                      <span className="font-semibold text-slate-700">
                        {formatEngagement(inf.engagementRate)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5 text-slate-400" />
                      <span className="font-semibold text-slate-700">
                        {formatMetric(inf.videoViews || inf.avgLikes)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Already added indicator */}
                {isAlreadyAssigned && (
                  <div
                    className="flex items-center shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-[10px] font-medium bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full border border-blue-100">
                      Already added
                    </span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Purple Information Callout Box */}
      <div className="mt-3.5 bg-[#FAF5FF] border border-[#F3E8FF] rounded-2xl p-3.5 flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-[#A655F8]/10 text-[#A655F8] flex items-center justify-center shrink-0">
          <Users className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs font-bold text-[#A655F8]">
            {activeSelectedCount} influencer
            {activeSelectedCount === 1 ? "" : "s"} will be added to the selected campaign.
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
            They will be added to the campaign's influencer list and you can manage deliverables, track content, and more.
          </p>
        </div>
      </div>
    </div>
  );
};
